import { CurrencyE67Enum, GuaranteeTypeEnum } from '@insurance/api/requests'
import { ConditionalText, DateTimeCell, Text } from '@shared/components'
import { getFormatCurrency } from '@shared/utils'
import { DescriptionsProps, Divider, Flex, Form } from 'antd'
import { Dayjs } from 'dayjs'
import { useTranslation } from 'react-i18next'

import { useStyles } from './styles'
import { GuaranteesPriceValue } from './type'
import {
  FIXED_BENEFICIARY_LABEL_KEY,
  formProps,
  GUARANTEE_TYPE_LABEL_KEY,
  hasFixedBeneficiary
} from './utils'
import { DetailsWrapper } from '../DetailsWrapper'

type ExtraDescriptions = (Required<DescriptionsProps>['items'][number] & {
  hidden?: boolean
})[]

export const Details = () => {
  const { t } = useTranslation()
  const { styles } = useStyles()

  const {
    priceKey,
    guaranteeTypeProps,
    validFromProps,
    validToProps,
    guaranteedObligationProps,
    currencyProps,
    guaranteedAmountProps,
    premiumProps,
    contractorCompanyNameProps,
    beneficiaryCompanyNameProps
  } = formProps(t)

  const price = Form.useWatch<GuaranteesPriceValue>(priceKey.name, {
    preserve: true
  })
  const guaranteeType = Form.useWatch<GuaranteeTypeEnum>(
    guaranteeTypeProps.name,
    { preserve: true }
  )
  const validFrom = Form.useWatch<Dayjs>(validFromProps.name, {
    preserve: true
  })
  const validTo = Form.useWatch<Dayjs>(validToProps.name, { preserve: true })
  const guaranteedObligation = Form.useWatch<string>(
    guaranteedObligationProps.name,
    { preserve: true }
  )
  const currency = Form.useWatch<CurrencyE67Enum>(currencyProps.name, {
    preserve: true
  })
  const guaranteedAmount = Form.useWatch<number>(guaranteedAmountProps.name, {
    preserve: true
  })
  const premium = Form.useWatch<number>(premiumProps.name, { preserve: true })
  const contractorName = Form.useWatch<string>(
    contractorCompanyNameProps.name,
    {
      preserve: true
    }
  )
  const beneficiaryName = Form.useWatch<string>(
    beneficiaryCompanyNameProps.name,
    { preserve: true }
  )

  const fixedBeneficiaryKey = guaranteeType
    ? FIXED_BENEFICIARY_LABEL_KEY[guaranteeType]
    : undefined

  const isFixedBeneficiary = hasFixedBeneficiary(guaranteeType)
  const isForeignCurrency = !!currency && currency !== CurrencyE67Enum.MDL

  const items: ExtraDescriptions = [
    {
      label: guaranteeTypeProps.label,
      children: (
        <ConditionalText condition={!!guaranteeType} className={styles.text}>
          {guaranteeType ? t(GUARANTEE_TYPE_LABEL_KEY[guaranteeType]) : ''}
        </ConditionalText>
      ),
      hidden: !guaranteeType
    },
    {
      label: validFromProps.label,
      children: (
        <DateTimeCell
          date={validFrom}
          hiddenTime
          dateProps={{ className: styles.date }}
        />
      ),
      hidden: !validFrom
    },
    {
      label: validToProps.label,
      children: (
        <DateTimeCell
          date={validTo}
          hiddenTime
          dateProps={{ className: styles.date }}
        />
      ),
      hidden: !validTo
    },
    {
      label: guaranteedObligationProps.label,
      children: (
        <ConditionalText
          condition={!!guaranteedObligation}
          className={styles.text}
        >
          {guaranteedObligation}
        </ConditionalText>
      ),
      hidden: !guaranteedObligation
    },
    {
      label: contractorCompanyNameProps.label,
      children: contractorName,
      hidden: !contractorName
    },
    {
      label: beneficiaryCompanyNameProps.label,
      children:
        isFixedBeneficiary && fixedBeneficiaryKey
          ? t(fixedBeneficiaryKey)
          : beneficiaryName,
      hidden: !isFixedBeneficiary && !beneficiaryName
    },
    {
      label: guaranteedAmountProps.label,
      children: getFormatCurrency({ amount: guaranteedAmount, currency }),
      hidden: !guaranteedAmount
    },
    {
      label: premiumProps.label,
      children: getFormatCurrency({ amount: premium, currency }),
      hidden: !premium
    },
    {
      label: t('exchange_rate_bnm'),
      children: price?.exchangeRate,
      hidden: !isForeignCurrency || !price?.exchangeRate
    },
    {
      label: t('exchange_rate_date'),
      children: (
        <DateTimeCell
          date={price?.exchangeRateDate}
          hiddenTime
          dateProps={{ className: styles.date }}
        />
      ),
      hidden: !isForeignCurrency || !price?.exchangeRateDate
    },
    {
      label: t('premium_mdl_equivalent'),
      children: getFormatCurrency({
        amount: price?.amountMdl,
        currency: CurrencyE67Enum.MDL
      }),
      hidden: !isForeignCurrency || !price?.amountMdl
    }
  ].filter(({ hidden }) => !hidden)

  return (
    <DetailsWrapper items={items}>
      {!!price?.amount && (
        <>
          <Flex align="center" justify="space-between">
            <Text strong size={18}>
              {t('total_to_be_paid')}
            </Text>
            <Text strong size={18} className="text-wrap-nowrap">
              {getFormatCurrency({
                amount: price.amount,
                currency: price.currency
              })}
            </Text>
          </Flex>

          <Divider />
        </>
      )}
    </DetailsWrapper>
  )
}
